import React, { useState, useEffect, useMemo, useRef } from "react";
import { printReports } from "../../utils";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";

import PrintIcon from "@mui/icons-material/Print";
import DownloadIcon from "@mui/icons-material/Download";
import RefreshIcon from "@mui/icons-material/Refresh";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import moment from "moment";

function IconButton({ children, title, onClick, disabled }) {
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      disabled={disabled}
      className={`tw-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-md tw-border tw-border-slate-300 tw-bg-white tw-text-xs tw-text-slate-700 hover:tw-bg-slate-50 ${
        disabled ? "tw-opacity-50 tw-cursor-not-allowed" : ""
      }`}
    >
      {children}
    </button>
  );
}

const SIGNALS_TO_PLOT = [
  { key: "/node0/port0", color: "#8884d8", name: "Analog", type: "monotone", yAxisId: "left" },
  { key: "/node0/port1", color: "#82ca9d", name: "Digital", type: "stepAfter", yAxisId: "right" },
  { key: "/node0/pwr", color: "#ffc658", name: "Power", type: "monotone", yAxisId: "left" },
  { key: "/node0/wifi_rssi", color: "#ff7300", name: "WIFI", type: "monotone", yAxisId: "left" },
];

export default function RawOnOffReport({ filters, range, fetchRawData, onRefresh, deviceSignalColors, onFavoriteClick, onDownloadCSV }) {
  const [rawData, setRawData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [currentDeviceIndex, setCurrentDeviceIndex] = useState(0);
  const printRef = useRef(null);

  const [visibleSignals, setVisibleSignals] = useState(
    SIGNALS_TO_PLOT.reduce((acc, signal) => ({ ...acc, [signal.key]: true }), {})
  );

  const handleDownloadCSV = () => {
     if (onDownloadCSV) onDownloadCSV();
  };

  const loadData = async () => {
    if (onRefresh) {
      const didUpdate = await onRefresh();
      if (didUpdate) return; 
    }

    if (fetchRawData) {
      setLoading(true);
      fetchRawData()
        .then((data) => {
          if (data && data.success) {
            setRawData(data.data);
            setCurrentDeviceIndex(0);
          } else {
            setRawData(null);
          }
        })
        .catch((err) => {
          console.error("Failed to fetch raw data", err);
          setRawData(null);
        })
        .finally(() => setLoading(false));
    }
  };

  useEffect(() => {
    if (fetchRawData) {
        setLoading(true);
        fetchRawData()
            .then((data) => {
            if (data && data.success) {
                setRawData(data.data);
                setCurrentDeviceIndex(0);
            } else {
                setRawData(null);
            }
            })
            .catch((err) => {
            console.error("Failed to fetch raw data", err);
            setRawData(null);
            })
            .finally(() => setLoading(false));
    }
  }, [filters, range, fetchRawData]);

  const deviceList = useMemo(() => {
    if (filters?.devices) {
      return filters.devices.split(',').map(d => d.trim()).filter(Boolean);
    }
    return rawData ? Object.keys(rawData) : [];
  }, [rawData, filters]);

  const currentDeviceName = deviceList[currentDeviceIndex];

  const signalsWithColors = useMemo(() => {
    const deviceColors = deviceSignalColors?.[currentDeviceName] || {};
    return SIGNALS_TO_PLOT.map(signal => ({
      ...signal,
      color: deviceColors[signal.key] || signal.color
    }));
  }, [currentDeviceName, deviceSignalColors]);

  const graphData = useMemo(() => {
    if (!currentDeviceName) return [];

    const deviceSignals = rawData ? rawData[currentDeviceName] : null;
    if (!deviceSignals) return [];

    const timeMap = new Map();

    signalsWithColors.forEach((signal) => {
      const points = deviceSignals[signal.key] || [];
      points.forEach((point) => {
        if (!timeMap.has(point.time)) {
          timeMap.set(point.time, { time: point.time });
        }
        const entry = timeMap.get(point.time);
        
        if (signal.yAxisId === "right") {
             entry[signal.key] = point.measure_value > 0 ? 1 : 0;
        } else {
            entry[signal.key] = (point.measure_value / 1023) * 100;
        }
      });
    });

    return Array.from(timeMap.values()).sort((a, b) => a.time - b.time);
  }, [rawData, currentDeviceName, signalsWithColors]);

  const xDomain = useMemo(() => {
    if (!filters?.durationStart || !range) return ['auto', 'auto'];
    const baseTime = moment(filters.durationStart).unix();
    const start = baseTime + range[0];
    const end = baseTime + range[1];
    return [start, end];
  }, [filters, range]);

  const xTicks = useMemo(() => {
    const [start, end] = xDomain;
    if (start === 'auto' || end === 'auto') return undefined;

    const count = 6;
    const step = (end - start) / (count - 1);
    const ticks = [];
    for (let i = 0; i < count; i++) {
      ticks.push(Math.round(start + i * step));
    }
    return ticks;
  }, [xDomain]);

  const handlePrevDevice = () => {
    setCurrentDeviceIndex((prev) => (prev > 0 ? prev - 1 : prev));
  };

  const handleNextDevice = () => {
    setCurrentDeviceIndex((prev) =>
      prev < deviceList.length - 1 ? prev + 1 : prev
    );
  };

  const handleLegendClick = (e) => {
    const { dataKey } = e;
    
    const isOnlyOneVisible = 
      visibleSignals[dataKey] && 
      Object.values(visibleSignals).filter(v => v).length === 1;

    if (isOnlyOneVisible) {
      setVisibleSignals(
        signalsWithColors.reduce((acc, signal) => ({ ...acc, [signal.key]: true }), {})
      );
    } else {
      setVisibleSignals(
        signalsWithColors.reduce((acc, signal) => ({ 
          ...acc, 
          [signal.key]: signal.key === dataKey 
        }), {})
      );
    }
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="tw-bg-white tw-p-2 tw-border tw-border-slate-200 tw-shadow-md tw-rounded tw-text-xs">
          <p className="tw-font-semibold tw-mb-1">
            {moment.unix(label).format("HH:mm:ss")}
          </p>
          {payload.map((entry, index) => {
             const signalConfig = signalsWithColors.find(s => s.key === entry.dataKey);
             const isDigital = signalConfig?.yAxisId === "right";
             const valueDisplay = isDigital ? entry.value : `${entry.value.toFixed(1)}%`;
             
             return (
                <div key={index} style={{ color: entry.color }}>
                  {entry.name}: {valueDisplay}
                </div>
             );
          })}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="tw-flex tw-flex-col tw-h-full tw-gap-4" ref={printRef}>
      <div className="tw-flex tw-items-center tw-justify-between tw-relative">
        <div className="tw-flex tw-items-center tw-gap-2">
            {deviceList.length > 0 ? (
                <>
                    <button 
                        onClick={handlePrevDevice} 
                        disabled={currentDeviceIndex === 0}
                        className="tw-p-1 hover:tw-bg-slate-100 tw-rounded disabled:tw-opacity-30"
                    >
                        <ChevronLeftIcon fontSize="small" />
                    </button>
                    <span className="tw-text-sm tw-font-medium tw-min-w-[150px] tw-text-center">
                        {currentDeviceName}
                    </span>
                    <button 
                        onClick={handleNextDevice} 
                        disabled={currentDeviceIndex === deviceList.length - 1}
                        className="tw-p-1 hover:tw-bg-slate-100 tw-rounded disabled:tw-opacity-30"
                    >
                        <ChevronRightIcon fontSize="small" />
                    </button>
                </>
            ) : (
                <span className="tw-text-sm tw-text-slate-500 tw-px-2">No devices</span>
            )}
        </div>
        
        <div className="tw-flex tw-items-center tw-gap-2">
            <IconButton title="Refresh Data" onClick={loadData}>
                 <RefreshIcon fontSize="small" className={loading ? "tw-animate-spin" : ""} />
            </IconButton>

          <IconButton title="Favourite" onClick={() => {
            console.log("Favorite Clicked in RawOnOffReport");
            if (onFavoriteClick) onFavoriteClick();
          }}>
            <FavoriteBorderIcon fontSize="small" />
          </IconButton>

          <IconButton title="Print chart" onClick={() => printReports(printRef.current)}>
            <PrintIcon fontSize="small" />
          </IconButton>

          <IconButton title="Download CSV" onClick={handleDownloadCSV}>
            <DownloadIcon fontSize="small" />
          </IconButton>
        </div>
      </div>

      <div className="tw-flex-1 tw-rounded-lg tw-bg-white tw-border tw-border-slate-200 tw-p-4 tw-flex tw-items-center tw-justify-center tw-min-h-[400px]">
        {loading ? (
            <div className="tw-text-slate-500">Loading...</div>
        ) : graphData.length > 0 ? (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={graphData}
              margin={{
                top: 5,
                right: 30,
                left: 20,
                bottom: 5,
              }}
            >
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e0e0e0" />
              <XAxis
                dataKey="time"
                type="number"
                domain={xDomain}
                ticks={xTicks}
                tickFormatter={(unixTime) => moment.unix(unixTime).format("HH:mm")}
                scale="time"
                tick={{ fontSize: 12 }}
              />
              <YAxis 
                yAxisId="left"
                domain={[0, 100]} 
                tickFormatter={(val) => `${val}%`}
                tick={{ fontSize: 12 }}
              />
              <YAxis 
                yAxisId="right"
                orientation="right"
                domain={[0, 1]} 
                ticks={[0, 1]}
                tick={{ fontSize: 12 }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend 
                onClick={handleLegendClick} 
                wrapperStyle={{ cursor: 'pointer' }}
              />
              {signalsWithColors.map((signal) => (
                <Line
                  key={signal.key}
                  yAxisId={signal.yAxisId}
                  type={signal.type}
                  dataKey={signal.key}
                  name={signal.key}
                  stroke={signal.color}
                  dot={false}
                  strokeWidth={2}
                  connectNulls
                  hide={!visibleSignals[signal.key]}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <span className="tw-text-xs tw-text-slate-400">
            {loading ? "Loading..." : "No data available for selected device"}
          </span>
        )}
      </div>
    </div>
  );
}
